import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

import { DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
// Disabling to enforce type:utils restrictions for device-behavior-core library
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DeviceImageProvider } from '@shure/shared/angular/ui/components/device-image';

import { DeviceImage } from './models';

export interface DeviceImagePluginOptions {
	/**
	 * Base path to assets.
	 *
	 * **Defaults to './assets/images'**
	 */
	assetsPath: string;

	/**
	 * Image to use as fallback if plugin for device model is not found.
	 *
	 * **Defaults to 'device-unknown.svg'**
	 */
	fallbackImage: string;
}

/*
 * Injection token that can be used to specify device image plugin options.
 */
// export const THEME_SERVICE = new InjectionToken<ThemeService>('__@shure/theming/theme.service');

/**
 * Injection token that can be used to specify device image plugin options.
 */
export const DEVICE_IMAGE_PLUGIN_OPTIONS = new InjectionToken<DeviceImagePluginOptions>(
	'__@shure/device-plugins/device-image-options__'
);

/**
 * Provides access to images for all registered devices and can be used by features
 * that has to show a image of a given device.
 */
@Injectable({ providedIn: 'root' })
export class DeviceImageBehaviorPluginService implements DeviceImageProvider {
	private readonly options: DeviceImagePluginOptions;

	constructor(@Inject(DEVICE_IMAGE_PLUGIN_OPTIONS) @Optional() options?: DeviceImagePluginOptions) {
		this.options = { assetsPath: './assets/images', fallbackImage: 'device-unknown.svg', ...options };
	}

	/**
	 * Get images associated with device. Converts the model name provided to a lowercase string with all
	 * spaces replaced with "-" and looks for SVG files in the "/assets/images/devices" directory that
	 * match that string. It will also find variants of the device image by appending a standard set
	 * of post-fixes to the base image name, such as "-line", "-back", etc.
	 * Example: IMX-Compute Console will get converted to imx-compute-console.svg
	 */
	public getImage(model: DeviceModel, channelCount?: number): DeviceImage {
		if (model === DeviceModel.UNKNOWN) {
			return this.getFallbackImage();
		}

		if (
			model === DeviceModel.IMXCOMPUTE ||
			model === DeviceModel.IMXCOMPUTEZOOM ||
			model === DeviceModel.P600 ||
			model === DeviceModel.MXWAPXD2
		) {
			channelCount = undefined;
		}

		const modelName = model.toLowerCase().replace(/\s/g, '-');

		return {
			lightsBackground: this.addPathToLightsBackgroundImages(`device-${modelName}.svg`),
			live: channelCount
				? this.addPathToDeviceImages(`device-${modelName}-${channelCount}.svg`)
				: this.addPathToDeviceImages(`device-${modelName}.svg`),
			virtual: channelCount
				? this.addPathToDeviceImages(`device-${modelName}-line-${channelCount}.svg`)
				: this.addPathToDeviceImages(`device-${modelName}-line.svg`),
			liveBack: this.addPathToDeviceImages(`device-${modelName}-back.svg`),
			virtualBack: this.addPathToDeviceImages(`device-${modelName}-back-line.svg`)
		};
	}

	/**
	 * Get fallback image as `DeviceImage`.
	 */
	private getFallbackImage(): DeviceImage {
		return {
			lightsBackground: this.addPathToLightsBackgroundImages(this.options.fallbackImage),
			live: this.addPathToDeviceImages(this.options.fallbackImage),
			virtual: this.addPathToDeviceImages(this.options.fallbackImage),
			liveBack: '',
			virtualBack: ''
		};
	}

	private addPathToLightsBackgroundImages(image: string): string {
		return `${this.options.assetsPath}/devices-lights/devices/${image}`;
	}

	private addPathToDeviceImages(image: string): string {
		return `${this.options.assetsPath}/devices/${image}`;
	}
}
